
import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, Check, Sparkles } from 'lucide-react';
import { ReconciliationRecord, BillingRecord } from '../../types';
import { api } from '../../services/api';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  record: ReconciliationRecord | null;
  currentMonth: string;
  onComplete: () => void;
}

const ManualMatchingModal: React.FC<Props> = ({ isOpen, onClose, record, currentMonth, onComplete }) => {
  const [billings, setBillings] = useState<BillingRecord[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [learnPattern, setLearnPattern] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrevMonth, setShowPrevMonth] = useState(false);

  // 前月（= 入金月の前々月に相当）の月文字列を算出
  const prevMonthStr = useMemo(() => {
    const [y, m] = currentMonth.replace(/-/g, '/').substring(0, 7).split('/').map(Number);
    const d = new Date(y, m - 2, 1);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, [currentMonth]);

  const currentMonthStr = currentMonth.replace(/-/g, '/').substring(0, 7);

  useEffect(() => {
    if (isOpen && record) {
      setSelectedIds(new Set());
      setSearchQuery('');
      setLearnPattern(false);
      setShowPrevMonth(false);
      setError(null);
      loadBillings(record);
    }
  }, [isOpen, record]);

  const loadBillings = async (rec: ReconciliationRecord) => {
    setIsLoading(true);
    try {
      let includeBillingIds: string[] | undefined;
      if (rec.type === 'matched') {
        const depositId = rec.deposit_id || rec.id;
        includeBillingIds = await api.reconciliation.getExistingMatchBillingsForDeposit(depositId);
        setSelectedIds(new Set(includeBillingIds));
      }
      const results = await api.reconciliation.getUnmatchedBillingsForManualMatch(
        currentMonth,
        includeBillingIds
      );
      // 選択済み（既存紐付け）の請求を先頭に表示
      if (includeBillingIds && includeBillingIds.length > 0) {
        const selectedSet = new Set(includeBillingIds);
        results.sort((a, b) => {
          const aSelected = selectedSet.has(a.id) ? 0 : 1;
          const bSelected = selectedSet.has(b.id) ? 0 : 1;
          if (aSelected !== bSelected) return aSelected - bSelected;
          return (a.client_name || '').localeCompare(b.client_name || '', 'ja');
        });
      }
      setBillings(results);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleBilling = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedBillings = billings.filter(b => selectedIds.has(b.id));
  const totalBillingAmount = selectedBillings.reduce((sum, b) => sum + b.billing_balance, 0);
  const depositAmount = record?.deposit_amount ?? 0;
  const diff = depositAmount - totalBillingAmount;

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const targetMonthForFilter = showPrevMonth ? prevMonthStr : currentMonthStr;

    const byMonth = billings.filter(b => {
      const bMonth = b.import_month?.replace(/-/g, '/').substring(0, 7);
      return bMonth === targetMonthForFilter;
    });

    const match = (b: BillingRecord) =>
      !searchQuery ||
      b.client_name.toLowerCase().includes(q) ||
      b.client_code.toLowerCase().includes(q) ||
      (b.client_kana || '').toLowerCase().includes(q);
    return {
      unmatched: byMonth.filter(b => !b.already_matched && match(b)),
      alreadyMatched: byMonth.filter(b => b.already_matched && match(b)),
    };
  }, [billings, searchQuery, showPrevMonth, currentMonthStr, prevMonthStr]);

  const handleSubmit = async () => {
    if (!record || selectedIds.size === 0) return;
    setIsSaving(true);
    setError(null);
    try {
      const depositId = record.deposit_id || record.id;

      await api.reconciliation.createManualMatches({
        depositId,
        depositAmount,
        billings: selectedBillings.map(b => ({
          id: b.id,
          billing_balance: b.billing_balance,
          client_code: b.client_code,
          client_name: b.client_name
        }))
      });

      // 1件選択時のみ学習機能を実行
      // amount=0 で保存することで「振込人名 → 得意先」のみを学習し、
      // 振込額が変動しても自動紐付けが効くようにする（業務担当者の運用要望）。
      // 金額固定で運用したい場合は、得意先管理画面の自動振分設定で手動編集可能。
      if (learnPattern && selectedBillings.length === 1 && record.remitter_name) {
        await api.clients.saveAutoRule({
          id: '',
          client_code: selectedBillings[0].client_code,
          client_name: selectedBillings[0].client_name,
          remitter_name: record.remitter_name,
          amount: 0, // 全金額対象（振込額に関係なく適用）
          notes: '学習機能により自動登録'
        });
        // 過去に同じ組み合わせを「紐付けをやめる」で解除していた場合、
        // 学習を選んだ意思を尊重して履歴を削除し、翌月以降の自動照合で紐付くようにする。
        // 履歴削除に失敗しても照合自体は成功しているため、警告のみ表示してフローを止めない。
        try {
          await api.reconciliation.removeUnmatchHistory(
            selectedBillings[0].client_code,
            record.remitter_name
          );
        } catch (err) {
          console.warn('履歴削除に失敗しました', err);
          setError('学習設定は保存されましたが、過去の解除履歴の削除に失敗しました。再度お試しください。');
        }
      }

      onComplete();
      onClose();
    } catch (e: any) {
      setError(e?.message || '照合処理に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !record) return null;

  const diffLabel = diff === 0
    ? '（完全一致）'
    : Math.abs(diff) <= 1000
      ? '（手数料範囲内）'
      : '';
  const diffColor = diff === 0 ? 'text-green-600' : Math.abs(diff) <= 1000 ? 'text-yellow-600' : 'text-red-500';

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg p-0 overflow-hidden animate-in zoom-in duration-200 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50 flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-800">手動照合</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X size={20}/></button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          {error && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 font-bold flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600"><X size={14}/></button>
            </div>
          )}

          {/* 固定：入金情報 */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <p className="text-xs font-black text-blue-400 uppercase mb-1 tracking-wider">入金（固定）</p>
            <p className="font-bold text-blue-900 text-sm">{record.remitter_name || record.client_name || '名称不明'}</p>
            <div className="flex gap-4 mt-1 text-sm">
              <span className="font-bold text-blue-700">¥{depositAmount.toLocaleString()}</span>
              <span className="text-blue-500">{record.deposit_date}</span>
            </div>
          </div>

          {/* 検索 */}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="得意先名・得意先CDで検索"
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>

          {/* 月切替タブ */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowPrevMonth(false)}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition ${
                !showPrevMonth
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-500 border-gray-300 hover:border-blue-300'
              }`}
            >
              {currentMonthStr}
            </button>
            <button
              type="button"
              onClick={() => setShowPrevMonth(true)}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition ${
                showPrevMonth
                  ? 'bg-orange-500 text-white border-orange-500'
                  : 'bg-white text-gray-500 border-gray-300 hover:border-orange-300'
              }`}
            >
              {prevMonthStr}（遅延払い）
            </button>
          </div>

          {/* 請求一覧 */}
          <div>
            <p className="text-xs font-black text-gray-400 uppercase mb-2 tracking-wider">照合する請求を選択</p>
            {isLoading ? (
              <p className="text-sm text-gray-400 py-4 text-center">読み込み中...</p>
            ) : filtered.unmatched.length === 0 && filtered.alreadyMatched.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">候補の請求データがありません</p>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                {filtered.unmatched.map(b => {
                  const isSelected = selectedIds.has(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => toggleBilling(b.id)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition text-sm ${
                        isSelected
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300 bg-white'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-mono text-xs text-gray-400">{b.client_code}</span>
                          <span className="ml-2 font-bold text-gray-800">{b.client_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-700">¥{b.billing_balance.toLocaleString()}</span>
                          {isSelected && <Check size={14} className="text-blue-600 flex-shrink-0" />}
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">{b.import_month}</p>
                    </button>
                  );
                })}
                {filtered.alreadyMatched.length > 0 && (
                  <>
                    <p className="text-xs font-bold text-orange-500 pt-1 pb-0.5">別入金と照合済みの請求（選択すると紐付けが移動します）</p>
                    {filtered.alreadyMatched.map(b => {
                      const isSelected = selectedIds.has(b.id);
                      return (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => toggleBilling(b.id)}
                          className={`w-full text-left p-3 rounded-lg border-2 transition text-sm ${
                            isSelected
                              ? 'border-orange-400 bg-orange-50'
                              : 'border-orange-200 hover:border-orange-300 bg-orange-50/40'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="font-mono text-xs text-gray-400">{b.client_code}</span>
                              <span className="ml-2 font-bold text-gray-700">{b.client_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-gray-600">¥{b.billing_balance.toLocaleString()}</span>
                              {isSelected && <Check size={14} className="text-orange-500 flex-shrink-0" />}
                            </div>
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">{b.import_month}</p>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>

          {/* 合計・差額 */}
          {selectedIds.size > 0 && (
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-500">入金額</span>
                <span className="font-bold">¥{depositAmount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">請求合計（{selectedIds.size}件）</span>
                <span className="font-bold">¥{totalBillingAmount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between border-t pt-1">
                <span className="text-gray-500">差額</span>
                <span className={`font-bold ${diffColor}`}>
                  ¥{diff.toLocaleString()}{diffLabel}
                </span>
              </div>
            </div>
          )}

          {/* 学習機能（1件選択時のみ） */}
          {selectedIds.size === 1 && (
            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-100 flex items-center gap-3">
              <input
                type="checkbox"
                id="learnPatternManual"
                checked={learnPattern}
                onChange={e => setLearnPattern(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <label htmlFor="learnPatternManual" className="text-xs font-bold text-yellow-800 cursor-pointer flex items-center gap-1.5 select-none">
                <Sparkles size={14} className="text-yellow-600" />
                この組み合わせを学習する（次回自動照合）
              </label>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-2 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 font-bold hover:bg-gray-50 transition"
          >
            キャンセル
          </button>
          <button
            onClick={handleSubmit}
            disabled={selectedIds.size === 0 || isSaving}
            className="px-8 py-2 text-sm bg-blue-600 text-white rounded-lg font-black hover:bg-blue-700 disabled:opacity-50 transition"
          >
            {isSaving ? '処理中...' : '照合する'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualMatchingModal;
